import RecoveryMinSecInputs from '../common/RecoveryMinSecInputs';
import PyramidSeriesEditor from '../common/PyramidSeriesEditor';
import TemplateRepsInputs from '../common/TemplateRepsInputs';
import {
  EditorCheckbox,
  EditorField,
  EditorSection,
  EditorUnitInput,
  GroupRecoveryPlaceholder,
  SetTypePicker,
  editorNumClass,
  editorTextClass,
} from '../common/ExerciseEditorParts';
import { getWorkoutSetTypeLabel, isRecoveryHiddenInGroup } from '../../../constants/workoutSetTypes';
import { isPyramidExercise } from '../../../constants/workoutSeriesPlan';

/**
 * Parametri di una riga esercizio nel template: stesse sezioni del pannello scheda
 * (parametri, tipo di serie, note per il cliente).
 */
export default function TemplateExerciseFields({
  exercise,
  index,
  onUpdate,
  selectedExercises,
  rowId,
  group,
}) {
  const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
  const pyramid = isPyramidExercise(exercise);
  const previous = index > 0 ? selectedExercises[index - 1] : null;
  const showLink = index > 0
    && exercise.set_type
    && exercise.set_type !== 'normal'
    && previous?.set_type === exercise.set_type;

  return (
    <div className="max-w-3xl space-y-5">
      <EditorSection title="Parametri">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          {!pyramid && (
            <EditorField label="Serie">
              <input
                type="number"
                min="1"
                value={isCardio ? (exercise.serie || 1) : (exercise.serie ?? '')}
                onChange={(e) => {
                  if (isCardio) {
                    onUpdate(index, 'serie', parseInt(e.target.value, 10) || 1);
                    return;
                  }
                  const value = e.target.value;
                  onUpdate(index, 'serie', value === '' ? '' : parseInt(value, 10) || '');
                }}
                onBlur={(e) => {
                  if (!isCardio && (e.target.value === '' || e.target.value === '0')) {
                    onUpdate(index, 'serie', 1);
                  }
                }}
                onFocus={(e) => e.target.select()}
                className={editorNumClass}
                aria-label="Serie"
              />
            </EditorField>
          )}

          {!pyramid && isCardio && (
            <EditorField label="Minuti">
              <input
                type="number"
                min="0"
                placeholder="20"
                value={exercise.ripetizioni_max}
                onChange={(e) => onUpdate(index, 'ripetizioni_max', parseInt(e.target.value, 10) || 0)}
                onFocus={(e) => e.target.select()}
                className={editorNumClass}
                aria-label="Minuti"
              />
            </EditorField>
          )}

          {!pyramid && !isCardio && (
            <TemplateRepsInputs
              exercise={exercise}
              onUpdate={(field, value) => onUpdate(index, field, value)}
            />
          )}

          {!pyramid && !isCardio && (
            <EditorField label="Peso">
              <EditorUnitInput
                unit="kg"
                min="0"
                step="0.5"
                value={exercise.peso_suggerito ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onUpdate(index, 'peso_suggerito', value === '' ? '' : parseFloat(value) || '');
                }}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    onUpdate(index, 'peso_suggerito', 0);
                  }
                }}
                onFocus={(e) => e.target.select()}
                aria-label="Peso (kg)"
              />
            </EditorField>
          )}

          {!isRecoveryHiddenInGroup(group) ? (
            <RecoveryMinSecInputs
              layout="templateGrid"
              templateFieldSize="sm"
              gymDarkInputs
              valueSeconds={exercise.tempo_recupero}
              onSecondsChange={(sec) => onUpdate(index, 'tempo_recupero', sec)}
              defaultTotalSeconds={90}
              focusColor="indigo"
            />
          ) : (
            <GroupRecoveryPlaceholder group={group} />
          )}
        </div>

        <PyramidSeriesEditor
          exercise={{ ...exercise, id: rowId, ripetizioni: exercise.ripetizioni_max, peso: exercise.peso_suggerito }}
          onUpdate={(_id, field, value) => onUpdate(index, field, value)}
          gymDarkInputs
        />
      </EditorSection>

      <EditorSection title="Tipo di serie">
        <SetTypePicker
          value={exercise.set_type}
          onChange={(value) => onUpdate(index, 'set_type', value)}
        />
        {(showLink || !isCardio) && (
          <div className="mt-3 flex flex-col gap-2">
            {showLink && (
              <EditorCheckbox
                checked={Boolean(exercise.linked_to_previous)}
                onChange={(checked) => onUpdate(index, 'linked_to_previous', checked)}
                hint={!exercise.linked_to_previous ? `Questo esercizio inizia un nuovo ${getWorkoutSetTypeLabel(exercise.set_type)}.` : null}
              >
                Unito al gruppo sopra
              </EditorCheckbox>
            )}
            {!isCardio && (
              <EditorCheckbox
                checked={Boolean(exercise.is_isometric_scheda)}
                onChange={(checked) => onUpdate(index, 'is_isometric_scheda', checked)}
              >
                Isometrico (secondi invece di ripetizioni)
              </EditorCheckbox>
            )}
          </div>
        )}
      </EditorSection>

      <EditorSection title="Note per il cliente">
        <textarea
          value={exercise.notes?.trainer ?? ''}
          onChange={(e) => {
            const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
            onUpdate(index, 'notes', {
              trainer: e.target.value || null,
              user: existingNotes.user || null,
              system: existingNotes.system || null,
            });
          }}
          rows={2}
          maxLength={500}
          className={editorTextClass}
          placeholder="Istruzioni per il cliente durante l'allenamento"
          aria-label="Note per il cliente"
        />
      </EditorSection>
    </div>
  );
}
